import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

export const DEFAULT_DATE_RANGE = 'this_month';

const DATE_RANGE_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_year', label: 'Last Year' },
];

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const parseYmd = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const sameDay = (a, b) => (
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate()
);

const monthCells = (year, month) => {
  const count = new Date(year, month + 1, 0).getDate();
  const cells = Array(new Date(year, month, 1).getDay()).fill(null);
  for (let day = 1; day <= count; day += 1) cells.push(new Date(year, month, day));
  while (cells.length % 7) cells.push(null);
  return cells;
};

const DateRangeFilter = ({
  value = DEFAULT_DATE_RANGE,
  onChange,
  customStart = '',
  customEnd = '',
  onCustomStartChange,
  onCustomEndChange,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState('');
  const [view, setView] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPicker = () => {
    const today = new Date();
    const focus = value === 'custom' ? (parseYmd(customStart) || today) : today;
    setView({ year: focus.getFullYear(), month: focus.getMonth() });
    setAnchor('');
    setOpen((current) => !current);
  };

  const pickPreset = (next) => {
    setAnchor('');
    onChange(next);
  };

  const pickDay = (date) => {
    const key = ymd(date);
    if (!anchor) {
      setAnchor(key);
      onCustomStartChange?.(key);
      onCustomEndChange?.('');
      if (value !== 'custom') onChange('custom');
      return;
    }
    const start = anchor <= key ? anchor : key;
    const end = anchor <= key ? key : anchor;
    onCustomStartChange?.(start);
    onCustomEndChange?.(end);
    if (value !== 'custom') onChange('custom');
    setAnchor('');
  };

  const shiftMonth = (step) => {
    setView((current) => {
      const next = new Date(current.year, current.month + step, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const today = new Date();
  const rangeStart = value === 'custom' ? parseYmd(anchor || customStart) : null;
  const rangeEnd = value === 'custom' && !anchor ? parseYmd(customEnd) : null;
  const cells = monthCells(view.year, view.month);

  return (
    <div ref={rootRef} className={`relative inline-flex shrink-0 justify-self-end ${className}`}>
      <button
        type="button"
        onClick={openPicker}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border text-blue-600 ${
          open ? 'border-blue-300 bg-blue-100' : 'border-blue-200 bg-blue-50 hover:bg-blue-100'
        }`}
        title="Date range"
        aria-label="Date range"
        aria-expanded={open}
      >
        <Calendar className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-[min(17.5rem,calc(100vw-1.5rem))] rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <div className="flex flex-wrap gap-1.5">
            {DATE_RANGE_OPTIONS.map((option) => {
              const selected = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => pickPreset(option.value)}
                  className={`rounded-md border px-2 py-1 text-xs leading-4 ${
                    selected
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between px-1">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="text-sm font-medium text-gray-800">
              {MONTHS[view.month]} {view.year}
            </span>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="mt-2 grid grid-cols-7 text-center text-[11px] text-gray-400">
            {WEEKDAYS.map((day, index) => (
              <span key={`${day}-${index}`} className="py-1">{day}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 text-center text-sm">
            {cells.map((date, index) => {
              if (!date) return <span key={`empty-${index}`} />;
              const key = ymd(date);
              const isStart = rangeStart && sameDay(date, rangeStart);
              const isEnd = rangeEnd && sameDay(date, rangeEnd);
              const inRange = rangeStart && rangeEnd && key >= ymd(rangeStart) && key <= ymd(rangeEnd);
              const isToday = sameDay(date, today);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickDay(date)}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full ${
                    isStart || isEnd
                      ? 'bg-blue-600 text-white'
                      : inRange
                        ? 'bg-blue-50 text-blue-700'
                        : isToday
                          ? 'text-gray-800 ring-1 ring-blue-500'
                          : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export function buildDateParams(dateRange, customStart, customEnd) {
  const params = {};
  if (dateRange && dateRange !== 'all_time') {
    params.date_range = dateRange;
    if (dateRange === 'custom') {
      if (customStart) params.from_date = customStart;
      if (customEnd) params.to_date = customEnd;
    }
  }
  return params;
}

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const monthStartMinus = (ref, months) => new Date(ref.getFullYear(), ref.getMonth() - months, 1);

/** from_date/to_date matching dashboard date_range (company settlement has no date_range arg). */
export function buildAbsoluteDateParams(dateRange, customStart, customEnd) {
  if (!dateRange || dateRange === 'all_time') return {};
  if (dateRange === 'custom') {
    const params = {};
    if (customStart) params.from_date = customStart;
    if (customEnd) params.to_date = customEnd;
    return params;
  }
  const today = new Date();
  const starts = {
    this_month: new Date(today.getFullYear(), today.getMonth(), 1),
    last_3_months: monthStartMinus(today, 3),
    last_6_months: monthStartMinus(today, 6),
    last_year: monthStartMinus(today, 12),
  };
  const start = starts[dateRange];
  if (!start) return {};
  return { from_date: ymd(start), to_date: ymd(today) };
}

/** Open the role's settlement work page with the same date window and hub filter as the dashboard. */
export function companySettlementApprovalsPath(dateRange, customStart, customEnd, hubIds, role) {
  const params = new URLSearchParams();
  const dates = buildAbsoluteDateParams(dateRange, customStart, customEnd);
  if (dates.from_date) params.set('from_date', dates.from_date);
  if (dates.to_date) params.set('to_date', dates.to_date);
  const hubs = (hubIds || []).map(String).filter(Boolean);
  if (hubs.length) params.set('hub_ids', hubs.join(','));
  const qs = params.toString();
  const base = role === 'Hub Admin' ? '/company-settlements' : '/company-settlement-approvals';
  return qs ? `${base}?${qs}` : base;
}

export default DateRangeFilter;
